import { useMemo } from 'react';

import parse, { DOMNode, Element } from 'html-react-parser';
import { marked } from 'marked';

import { ArtifactBlock } from './artifact-block';
import { EndBlock } from './end-block';
import { Reference } from './reference';
import { UserMessage } from './user-message';
import {
  ArtifactBlock as ArtifactBlockType,
  ContentBlock,
  KimMessageType,
  ReferenceType,
} from '../../../../../../../../../../../backend/src/types/types';
import { KimMessageFile } from '@/api/types/backend-dto-types';
import { cn } from '@/new/lib/utils';

export const REF_PLACEHOLDER_TAG = 'kimref';
const ARTIFACT_PLACEHOLDER_TAG = 'kimartifact';

export function formatContent(blocks: ContentBlock[]): string {
  return blocks
    .map((block) => {
      if (block.type === 'text') return block.value;
      if (block.type === 'artifact')
        return `<${ARTIFACT_PLACEHOLDER_TAG} data-id="${block.id}-${block.versionNumber}"></${ARTIFACT_PLACEHOLDER_TAG}>`;
      return `<${REF_PLACEHOLDER_TAG} data-id="${block.id}" data-ref-type="${block.refType}"></${REF_PLACEHOLDER_TAG}>`;
    })
    .join('');
}

export function KimMessage({
  type,
  content,
  isComplete,
  referenceIndexMap,
  onReferenceClick,
  onDownloadClick,
  artifactsById,
  latestArtifactId,
  isDownloading,
  createdAt,
  files,
  isOwnThread,
}: {
  type: KimMessageType;
  content: ContentBlock[];
  isComplete: boolean;
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  onDownloadClick?: (artifactId?: string) => void;
  artifactsById?: Map<string, ArtifactBlockType & { published: boolean | undefined }>;
  latestArtifactId?: string | null;
  isDownloading?: boolean;
  createdAt?: string;
  files?: KimMessageFile[];
  isOwnThread?: boolean;
}) {
  const isUser = type === KimMessageType.User;

  const factIds = useMemo(() => {
    const ids = new Set<string>();
    for (const block of content) {
      if (block.type === 'reference' && block.refType === ReferenceType.Fact) {
        ids.add(block.id);
      }
      if (block.type === 'artifact') {
        block.content.forEach((artifact) => {
          if (artifact.type === 'reference' && artifact.refType === ReferenceType.Fact) {
            ids.add(artifact.id);
          }
        });
      }
    }
    return Array.from(ids);
  }, [content]);

  const segments = useMemo(() => {
    type TextSegment = { type: 'text'; blocks: ContentBlock[] };
    type ArtifactSegment = { type: 'artifact'; id: string; artifact: ArtifactBlockType };
    type Segment = TextSegment | ArtifactSegment;

    const result: Segment[] = [];
    let textBlocks: ContentBlock[] = [];

    for (const block of content) {
      if (block.type === 'artifact') {
        if (textBlocks.length > 0) {
          result.push({ type: 'text', blocks: textBlocks });
          textBlocks = [];
        }
        const id = `${block.id}-${block.versionNumber}`;
        const artifact = artifactsById?.get(id);
        if (artifact) result.push({ type: 'artifact', id, artifact });
      } else {
        textBlocks.push(block);
      }
    }
    if (textBlocks.length > 0) result.push({ type: 'text', blocks: textBlocks });

    return result.map((segment, i) => {
      if (segment.type === 'artifact') {
        return (
          <ArtifactBlock
            key={segment.id}
            id={segment.id}
            content={segment.artifact.content}
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={onReferenceClick}
            onDownloadClick={onDownloadClick ? () => onDownloadClick(segment.id) : undefined}
            collapsed={segment.id !== latestArtifactId}
            version={segment.artifact.versionNumber}
            isDownloading={isDownloading}
            artifactsById={artifactsById}
            isOwnThread={isOwnThread}
          />
        );
      }

      const combined = formatContent(segment.blocks);
      const rawHtml = marked.parse(combined, { async: false }) as string;
      const html = rawHtml.replace(
        new RegExp(`(<\\/${REF_PLACEHOLDER_TAG}>)\\s+(<${REF_PLACEHOLDER_TAG})`, 'g'),
        '$1$2',
      );
      const parsed = parse(html, {
        replace: (domNode: DOMNode) => {
          if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
            const id = domNode.attribs['data-id'];
            const refType = domNode.attribs['data-ref-type'] as ReferenceType;
            return (
              <Reference
                index={referenceIndexMap.get(id)?.index ?? 0}
                id={id}
                referenceType={refType}
                onClick={
                  refType === ReferenceType.Fact ||
                  refType === ReferenceType.Chunk ||
                  refType === ReferenceType.Document
                    ? onReferenceClick
                    : undefined
                }
              />
            );
          }
        },
      });

      return (
        <div
          key={i}
          className={cn(type === KimMessageType.Ai && 'px-4', 'mx-auto flex max-w-3xl items-start gap-3.25')}
        >
          {i === 0 && type === KimMessageType.AiReasoning && (
            <div className="mt-2.25 size-1.25 shrink-0 rounded-full bg-gray-300" />
          )}
          <div
            className={cn(
              'prose prose-sm prose-headings:my-3 prose-a:hover:text-blue-600 prose-a:duration-150 prose-a:text-blue-500 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-hr:my-6 prose-p:leading-normal prose-p:text-gray-900 prose-headings:font-semibold max-w-none',
              type === KimMessageType.AiReasoning && 'prose-p:text-gray-600 italic',
            )}
          >
            {parsed}
          </div>
        </div>
      );
    });
  }, [
    content,
    artifactsById,
    referenceIndexMap,
    onReferenceClick,
    onDownloadClick,
    latestArtifactId,
    isDownloading,
    type,
  ]);

  const getTextToCopy = () =>
    content
      .filter((block) => block.type === 'text')
      .map((block) => block.value)
      .join('');

  if (isUser) {
    return <UserMessage content={content} createdAt={createdAt} files={files} />;
  }

  return (
    <div className="flex">
      <div
        className={cn(
          // the [font-feature-settings:\'calt\'_0] disables converting (1) into a weird unicode char
          "w-full rounded-xl py-2 [font-feature-settings:'calt'_0]",
        )}
      >
        <div className="font-serif">{segments}</div>

        {type === KimMessageType.Ai && isComplete && (
          <EndBlock
            getTextToCopy={getTextToCopy}
            factIds={factIds}
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={onReferenceClick}
            onDownloadClick={onDownloadClick ? () => onDownloadClick() : undefined}
            isDownloading={isDownloading}
          />
        )}
      </div>
    </div>
  );
}
